import React, {useEffect, useState} from 'react';
import {useMutation, useQuery} from '@tanstack/react-query';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import PageHeader from '@/components/layout/PageHeader';
import {useWorkspace} from '@/context/WorkspaceContext';
import {
  WyscoutImportMonths,
  WyscoutPlayerSearchResult,
  wyscoutImportAPI,
} from '@/features/players/wyscoutImportApi';
import {parsePlayerReturnTo} from '@/features/players/playerUrlState';
import {getV2Error} from '@/services/api/v2';

const detailHref = (playerId: string, returnPath: string) => `/scouting/${playerId}?${new URLSearchParams({from: returnPath})}`;
const competitionNames: Record<number, string> = {
  350: 'National League North/South',
  351: 'National League',
  359: 'League Two',
  361: 'Non League Premier',
};

const formatBirthDate = (value: string): string => {
  if (!value) return 'Not provided';
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return 'Not provided';
  return new Intl.DateTimeFormat('en-GB', {day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'}).format(date);
};

const PlayerResultCard: React.FC<{
  player: WyscoutPlayerSearchResult;
  months: WyscoutImportMonths;
  importing: boolean;
  importDisabled: boolean;
  returnPath: string;
  onImport: () => void;
}> = ({player, months, importing, importDisabled, returnPath, onImport}) => (
  <article
    aria-label={player.name}
    className="min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-5"
  >
    <div className="flex min-w-0 items-start gap-4">
      {player.imageUrl ? (
        <img className="h-16 w-16 shrink-0 rounded-lg object-cover" src={player.imageUrl} alt="" />
      ) : (
        <div aria-hidden="true" className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-club-navy-950 text-xl font-bold text-white">
          {player.name.trim().charAt(0).toUpperCase() || '?'}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <h2 className="break-words text-lg font-bold text-gray-900 dark:text-gray-100">{player.name}</h2>
        <p className="mt-1 break-words text-sm text-gray-700 dark:text-gray-300">
          {[player.position, player.teamName].filter(Boolean).join(' | ') || 'Profile details unavailable'}
        </p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Born {formatBirthDate(player.birthDate)}</p>
      </div>
    </div>

    <div className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Named competition coverage</p>
      {player.competitionIds.length > 0 ? (
        <ul aria-label={`${player.name} competitions`} className="mt-2 flex flex-wrap gap-2">
          {player.competitionIds.map(id => (
            <li key={id} className="rounded-full bg-club-blue-50 px-2.5 py-1 text-xs font-semibold text-club-blue-800 dark:bg-club-blue-950 dark:text-club-blue-200">
              {competitionNames[id] ?? `Competition ${id}`}
            </li>
          ))}
        </ul>
      ) : <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Competition names are not available.</p>}
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
        {player.seasonIds.length} {player.seasonIds.length === 1 ? 'season' : 'seasons'} found within the selected history period.
      </p>
    </div>

    <div className="mt-4 flex flex-wrap gap-2">
      {player.existingPlayerId && (
        <Link
          to={detailHref(player.existingPlayerId, returnPath)}
          className="inline-flex min-h-11 items-center rounded-md border border-gray-300 px-4 text-sm font-semibold text-club-blue-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-600 dark:text-club-blue-300 dark:hover:bg-gray-800"
        >
          View existing player
        </Link>
      )}
      <button
        type="button"
        disabled={importDisabled}
        onClick={onImport}
        className="inline-flex min-h-11 items-center rounded-md bg-club-blue-700 px-4 text-sm font-semibold text-white hover:bg-club-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {importing
          ? `Importing ${player.name}`
          : player.existingPlayerId ? `Import ${months} months for ${player.name}` : `Import ${player.name}`}
      </button>
    </div>
  </article>
);

const WyscoutPlayerImportPage: React.FC = () => {
  const {currentWorkspace} = useWorkspace();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnPath = parsePlayerReturnTo(searchParams.get('from'), 'scouting');
  const [months, setMonths] = useState<WyscoutImportMonths>(12);
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [importingPlayerId, setImportingPlayerId] = useState<number>();

  useEffect(() => {
    const trimmed = search.trim();
    if (Array.from(trimmed).length < 3) {
      setCommittedSearch('');
      return;
    }
    const timer = window.setTimeout(() => setCommittedSearch(trimmed), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const query = useQuery({
    queryKey: ['wyscout-player-search', currentWorkspace?.id ?? '', committedSearch, months],
    queryFn: ({signal}) => wyscoutImportAPI.search(currentWorkspace?.id ?? '', committedSearch, months, signal),
    enabled: Boolean(currentWorkspace?.id && committedSearch),
  });

  const create = useMutation({
    mutationFn: (wyscoutPlayerId: number) => wyscoutImportAPI.create(currentWorkspace?.id ?? '', wyscoutPlayerId, months),
    onSuccess: run => navigate(detailHref(run.playerId, returnPath)),
    onError: error => {
      const playerId = getV2Error(error).details.player_id;
      if (typeof playerId === 'string') navigate(detailHref(playerId, returnPath));
    },
    onSettled: () => setImportingPlayerId(undefined),
  });

  const beginImport = (player: WyscoutPlayerSearchResult) => {
    if (create.isPending) return;
    setImportingPlayerId(player.wyscoutPlayerId);
    create.mutate(player.wyscoutPlayerId);
  };

  const trimmedLength = Array.from(search.trim()).length;
  const createError = create.isError && getV2Error(create.error).details.player_id === undefined
    ? getV2Error(create.error)
    : undefined;

  return (
    <div className="min-w-0 space-y-6">
      <PageHeader
        title="Import from WyScout"
        subtitle="Find a player and create a scouting profile while their recent match history imports in the background."
        actions={<Link to={returnPath} className="inline-flex min-h-11 items-center rounded-md border border-gray-300 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800">Back to Scouting</Link>}
      />

      <section role="search" aria-label="WyScout player search" className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-5">
        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200">
          History to import
          <select
            value={months}
            onChange={event => setMonths(Number(event.target.value) as WyscoutImportMonths)}
            className="mt-1 block min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 sm:max-w-xs"
          >
            <option value={12}>12 months</option>
            <option value={24}>24 months</option>
            <option value={36}>36 months</option>
          </select>
        </label>
        <label className="mt-4 block text-sm font-semibold text-gray-800 dark:text-gray-200">
          Search WyScout players
          <input
            type="search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Player name"
            autoComplete="off"
            className="mt-1 block min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-gray-900 placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
        </label>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Search by player name. Enter at least 3 characters to search.
        </p>
      </section>

      <section aria-label="WyScout player results" className="min-w-0 space-y-4">
        {query.data && (
          <p role="status" aria-live="polite" aria-label="WyScout search results" className="sr-only">
            {query.data.data.length === 0
              ? '0 players found. No WyScout players matched your search.'
              : `${query.data.data.length} ${query.data.data.length === 1 ? 'player' : 'players'} found.`}
          </p>
        )}
        {query.isFetching && !query.data && (
          <div role="status" aria-label="Searching WyScout players" className="rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
            Searching WyScout players...
          </div>
        )}

        {query.isError && (
          <div role="alert" className="rounded-xl border border-danger-300 bg-danger-50 p-4 text-danger-900 dark:border-danger-700 dark:bg-danger-950 dark:text-danger-100">
            <p>{getV2Error(query.error).message}</p>
            <button type="button" onClick={() => { void query.refetch(); }} className="mt-2 min-h-11 font-semibold underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500">Try search again</button>
          </div>
        )}

        {createError && <div role="alert" className="rounded-xl border border-danger-300 bg-danger-50 p-4 text-danger-900 dark:border-danger-700 dark:bg-danger-950 dark:text-danger-100">{createError.message}</div>}

        {query.data?.warnings.map((warning: string) => (
          <div key={warning} role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
            {warning} Results below are the coverage WyScout could return.
          </div>
        ))}

        {query.data && query.data.data.length === 0 && (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200">
            No WyScout players matched your search.
          </div>
        )}

        {query.data && query.data.data.length > 0 && (
          <div className="grid min-w-0 gap-4 lg:grid-cols-2">
            {query.data.data.map((player: WyscoutPlayerSearchResult) => (
              <PlayerResultCard
                key={player.wyscoutPlayerId}
                player={player}
                months={months}
                importing={create.isPending && importingPlayerId === player.wyscoutPlayerId}
                importDisabled={create.isPending}
                returnPath={returnPath}
                onImport={() => beginImport(player)}
              />
            ))}
          </div>
        )}

        {!committedSearch && !query.isFetching && !query.data && trimmedLength < 3 && (
          <p className="text-center text-sm text-gray-600 dark:text-gray-300">Enter at least 3 characters to search.</p>
        )}
      </section>
    </div>
  );
};

export default WyscoutPlayerImportPage;
